/*
 * Thin Firestore helpers (SPEC §10). Public reads use realtime onSnapshot; all
 * helpers no-op (return an empty unsubscribe) when Firebase isn't configured, so
 * callers can subscribe unconditionally.
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

const noop = () => {};

// Realtime ordered collection (order asc). cb receives an array of {id, ...data}.
export function subscribeOrderedCollection(name, cb) {
  if (!db) return noop;
  const q = query(collection(db, name), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.warn(`[firestore] ${name}:`, err.message)
  );
}

// Realtime single doc. cb receives the data object or null if missing.
export function subscribeDoc(path, cb) {
  if (!db) return noop;
  const [c, id] = path.split('/');
  return onSnapshot(
    doc(db, c, id),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => console.warn(`[firestore] ${path}:`, err.message)
  );
}

// One-shot reads (used by admin lists where realtime isn't needed).
export async function fetchOrderedCollection(name) {
  if (!db) return [];
  const snap = await getDocs(query(collection(db, name), orderBy('order', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchDoc(path) {
  if (!db) return null;
  const [c, id] = path.split('/');
  const snap = await getDoc(doc(db, c, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Writes (admin). All stamp updatedAt.
export async function createInCollection(name, data) {
  return addDoc(collection(db, name), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}
export async function updateInCollection(name, id, data) {
  return updateDoc(doc(db, name, id), { ...data, updatedAt: serverTimestamp() });
}
export async function deleteFromCollection(name, id) {
  return deleteDoc(doc(db, name, id));
}
export async function writeSettingsDoc(id, data) {
  return setDoc(doc(db, 'settings', id), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

// Persist a new ordering: writes order = index+1 for each id.
export async function reorderDocs(name, idsInOrder) {
  await Promise.all(
    idsInOrder.map((id, i) => updateDoc(doc(db, name, id), { order: i + 1, updatedAt: serverTimestamp() }))
  );
}

export { serverTimestamp };
